import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface Subscription {
  id: string
  subscriptionNumber: string
  name: string
  description?: string
  customer: {
    id: string
    name: string
  }
  startDate: string
  endDate?: string
  billingCycle: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
  billingDay?: number
  amount: string
  currency: string
  taxRate?: {
    id: string
    name: string
    rate: string
  }
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'EXPIRED'
  autoRenew: boolean
  trialDays?: number
  trialEndDate?: string
  nextBillingDate?: string
  lastBillingDate?: string
  cancelledAt?: string
  cancellationReason?: string
  notes?: string
}

interface ClientOrganization {
  id: string
  name: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
}

interface Account {
  id: string
  code: string
  name: string
}

const Subscriptions: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [customers, setCustomers] = useState<ClientOrganization[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customerId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    billingCycle: 'MONTHLY' as const,
    billingDay: '',
    amount: '',
    currency: 'GBP',
    taxRateId: '',
    autoRenew: true,
    trialDays: '',
    invoiceAccountId: '',
    notes: '',
  })

  useEffect(() => {
    fetchSubscriptions()
    fetchCustomers()
    fetchTaxRates()
    fetchAccounts()
  }, [])

  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/subscriptions')
      setSubscriptions(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching subscriptions:', error)
      toast.error('Failed to load subscriptions')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/client-organizations')
      setCustomers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates')
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts')
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const handleCreateSubscription = async () => {
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        billingDay: formData.billingDay ? parseInt(formData.billingDay) : undefined,
        trialDays: formData.trialDays ? parseInt(formData.trialDays) : undefined,
        endDate: formData.endDate || null,
      }
      await axios.post('/api/erp/subscriptions', payload)
      toast.success('Subscription created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchSubscriptions()
    } catch (error: any) {
      console.error('Error creating subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to create subscription')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      customerId: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      billingCycle: 'MONTHLY',
      billingDay: '',
      amount: '',
      currency: 'GBP',
      taxRateId: '',
      autoRenew: true,
      trialDays: '',
      invoiceAccountId: '',
      notes: '',
    })
  }

  const handleActivate = async (id: string) => {
    if (!confirm('Are you sure you want to activate this subscription?')) return
    try {
      await axios.post(`/api/erp/subscriptions/${id}/activate`)
      toast.success('Subscription activated successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        const response = await axios.get(`/api/erp/subscriptions/${id}`)
        setSelectedSubscription(response.data.data)
      }
    } catch (error: any) {
      console.error('Error activating subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to activate subscription')
    }
  }

  const handlePause = async (id: string) => {
    if (!confirm('Are you sure you want to pause this subscription?')) return
    try {
      await axios.post(`/api/erp/subscriptions/${id}/pause`)
      toast.success('Subscription paused successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        const response = await axios.get(`/api/erp/subscriptions/${id}`)
        setSelectedSubscription(response.data.data)
      }
    } catch (error: any) {
      console.error('Error pausing subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to pause subscription')
    }
  }

  const handleResume = async (id: string) => {
    if (!confirm('Are you sure you want to resume this subscription?')) return
    try {
      await axios.post(`/api/erp/subscriptions/${id}/resume`)
      toast.success('Subscription resumed successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        const response = await axios.get(`/api/erp/subscriptions/${id}`)
        setSelectedSubscription(response.data.data)
      }
    } catch (error: any) {
      console.error('Error resuming subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to resume subscription')
    }
  }

  const handleCancel = async (id: string) => {
    const reason = prompt('Please provide a reason for cancellation:')
    if (!reason) return
    if (!confirm('Are you sure you want to cancel this subscription?')) return
    try {
      await axios.post(`/api/erp/subscriptions/${id}/cancel`, { reason })
      toast.success('Subscription cancelled successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        const response = await axios.get(`/api/erp/subscriptions/${id}`)
        setSelectedSubscription(response.data.data)
      }
    } catch (error: any) {
      console.error('Error cancelling subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel subscription')
    }
  }

  const handleGenerateInvoice = async (id: string) => {
    if (!confirm('Generate invoice for this subscription?')) return
    try {
      await axios.post(`/api/erp/subscriptions/${id}/generate-invoice`)
      toast.success('Invoice generated successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        const response = await axios.get(`/api/erp/subscriptions/${id}`)
        setSelectedSubscription(response.data.data)
      }
    } catch (error: any) {
      console.error('Error generating invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to generate invoice')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this subscription?')) return
    try {
      await axios.delete(`/api/erp/subscriptions/${id}`)
      toast.success('Subscription deleted successfully')
      fetchSubscriptions()
      if (selectedSubscription?.id === id) {
        setShowViewModal(false)
        setSelectedSubscription(null)
      }
    } catch (error: any) {
      console.error('Error deleting subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to delete subscription')
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_subscription', 'read')) {
    return <div className="p-6">You don't have permission to access this page.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Subscription Management</h1>
        <PageHelp articleKey="erp_subscription" />
      </div>

      <div className="mb-4 flex justify-end">
        {hasPermission('erp_subscription', 'write') && (
          <button
            onClick={() => {
              setSelectedSubscription(null)
              resetForm()
              setShowCreateModal(true)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Create Subscription
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading subscriptions...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Cycle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {subscription.subscriptionNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subscription.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscription.customer.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscription.billingCycle}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscription.currency} {parseFloat(subscription.amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscription.nextBillingDate
                      ? new Date(subscription.nextBillingDate).toLocaleDateString()
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        subscription.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : subscription.status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : subscription.status === 'DRAFT'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {subscription.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedSubscription(subscription)
                        setShowViewModal(true)
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      View
                    </button>
                    {subscription.status === 'DRAFT' && hasPermission('erp_subscription', 'write') && (
                      <button
                        onClick={() => handleActivate(subscription.id)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Activate
                      </button>
                    )}
                    {subscription.status === 'ACTIVE' && hasPermission('erp_subscription', 'write') && (
                      <>
                        <button
                          onClick={() => handlePause(subscription.id)}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                        >
                          Pause
                        </button>
                        <button
                          onClick={() => handleGenerateInvoice(subscription.id)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                        >
                          Generate Invoice
                        </button>
                      </>
                    )}
                    {subscription.status === 'PAUSED' && hasPermission('erp_subscription', 'write') && (
                      <button
                        onClick={() => handleResume(subscription.id)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Resume
                      </button>
                    )}
                    {(subscription.status === 'ACTIVE' || subscription.status === 'PAUSED') &&
                      hasPermission('erp_subscription', 'write') && (
                        <button
                          onClick={() => handleCancel(subscription.id)}
                          className="text-red-600 hover:text-red-900 mr-3"
                        >
                          Cancel
                        </button>
                      )}
                    {subscription.status === 'DRAFT' && hasPermission('erp_subscription', 'delete') && (
                      <button
                        onClick={() => handleDelete(subscription.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {subscriptions.length === 0 && (
            <div className="text-center py-8 text-gray-500">No subscriptions found</div>
          )}
        </div>
      )}

      {/* Create Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Subscription</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle *</label>
                  <select
                    value={formData.billingCycle}
                    onChange={(e) =>
                      setFormData({ ...formData, billingCycle: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Day</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.billingDay}
                    onChange={(e) => setFormData({ ...formData, billingDay: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Day of month/week"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate</label>
                  <select
                    value={formData.taxRateId}
                    onChange={(e) => setFormData({ ...formData, taxRateId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">No Tax</option>
                    {taxRates.map((rate) => (
                      <option key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Account</label>
                  <select
                    value={formData.invoiceAccountId}
                    onChange={(e) => setFormData({ ...formData, invoiceAccountId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                  <input
                    type="number"
                    value={formData.trialDays}
                    onChange={(e) => setFormData({ ...formData, trialDays: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Trial period in days"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.autoRenew}
                    onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Auto Renew</label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSubscription}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Subscription Modal */}
      {showViewModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Subscription Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Number</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.subscriptionNumber}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.customer.name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        selectedSubscription.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : selectedSubscription.status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : selectedSubscription.status === 'DRAFT'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {selectedSubscription.status}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.billingCycle}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.currency} {parseFloat(selectedSubscription.amount).toFixed(2)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {new Date(selectedSubscription.startDate).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Billing Date</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.nextBillingDate
                      ? new Date(selectedSubscription.nextBillingDate).toLocaleDateString()
                      : '-'}
                  </div>
                </div>
              </div>
              {selectedSubscription.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.description}
                  </div>
                </div>
              )}
              {selectedSubscription.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedSubscription.notes}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-6">
                {selectedSubscription.status === 'DRAFT' && hasPermission('erp_subscription', 'write') && (
                  <button
                    onClick={() => handleActivate(selectedSubscription.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Activate
                  </button>
                )}
                {selectedSubscription.status === 'ACTIVE' && hasPermission('erp_subscription', 'write') && (
                  <>
                    <button
                      onClick={() => handlePause(selectedSubscription.id)}
                      className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                    >
                      Pause
                    </button>
                    <button
                      onClick={() => handleGenerateInvoice(selectedSubscription.id)}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      Generate Invoice
                    </button>
                  </>
                )}
                {selectedSubscription.status === 'PAUSED' && hasPermission('erp_subscription', 'write') && (
                  <button
                    onClick={() => handleResume(selectedSubscription.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Resume
                  </button>
                )}
                {(selectedSubscription.status === 'ACTIVE' || selectedSubscription.status === 'PAUSED') &&
                  hasPermission('erp_subscription', 'write') && (
                    <button
                      onClick={() => handleCancel(selectedSubscription.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Cancel
                    </button>
                  )}
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedSubscription(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Subscriptions
